import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { MailIcon } from 'lucide-react';
import { SUPPORT_EMAIL } from '../data/launch';

/**
 * INTERNAL NOTE — not rendered.
 * Both the in-app navigation path and the backend deletion behaviour are now
 * CONFIRMED against production:
 *  - path: Home → Settings → Support → Delete Account
 *  - soft delete / anonymisation. Sign-in blocked; phone, date of birth,
 *    profile picture, auth details and saved bank details removed; shared
 *    expense, settlement and membership records may remain, along with a
 *    historical display name and receipts attached to shared expenses; a
 *    limited hashed identifier may be retained to recognise a previously
 *    deleted account.
 * Still NOT confirmed: how long deletion takes end to end. Do not state a
 * timeframe. See docs/internal-pre-launch-checklist.md.
 */
const steps = [{
  title: 'Open KittyPot and sign in.'
}, {
  title: 'From Home, go to Settings → Support → Delete Account.'
}, {
  title: 'Confirm.',
  detail: 'Your account information is removed or anonymised and the account can no longer be used. Records that form part of a shared kitty’s history may remain so other members’ balances stay accurate.'
}];
export function DeleteAccount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-479" data-mp="1ientmlgwwyyy">
      <SEO title="Delete your KittyPot account | KittyPot" description="How to permanently delete your KittyPot account from the app, and what happens to your data when you do." ogTitle="Delete your KittyPot account" ogDescription="How to permanently delete your KittyPot account and what happens to your data." path="/delete-account" data-id="element-480" data-mp="14hs5rmw052yu" />
      <Navbar data-id="element-481" data-mp="3a23ox0louppe" />
      <main className="pt-36 pb-24" data-id="element-482" data-mp="3a4zkiia2d2ao">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-483" data-mp="360ry4rr2vyfm">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-484" data-mp="363nuibna2f1y">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-5 tracking-tight" data-id="element-485" data-mp="2wap20ionu4d2">
              Delete your KittyPot account
            </h1>
            <p className="text-lg text-gray-500 mb-8 max-w-xl" data-id="element-486" data-mp="2mdkfghqwk6vq">
              You can delete your KittyPot account at any time, from inside the
              app or by asking us. There are two routes below.
            </p>

            <div className="mb-12 rounded-2xl border border-gray-200 bg-white p-6 md:p-7" data-id="element-487" data-mp="2ddnzd4ikwgca">
              <h2 className="text-lg font-bold text-kitty-dark mb-3" data-id="element-488" data-mp="gkm70xyaix25">
                What deleting your account means
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-489" data-mp="bm124l83fp7d">
                Deleting your account removes or anonymises your account
                information and prevents the account from being used again. Some
                information that forms part of a shared kitty's expense or
                settlement history may remain, so the records of other group
                members continue to work correctly.
              </p>
              <ul className="list-disc pl-5 space-y-2 text-gray-600 leading-relaxed" data-id="element-490" data-mp="6675j006ib3">
                <li data-id="element-491" data-mp="54zmzi2rilef">
                  You will no longer be able to sign in. Your phone number, date
                  of birth, profile picture, sign-in details and any saved bank
                  or payment details are removed, along with any push
                  notification registration.
                </li>
                <li data-id="element-492" data-mp="1ljsgesbk341v">
                  Shared expense, settlement and membership records may remain in
                  the kitties you took part in, so other members' balances and
                  history still add up.
                </li>
                <li data-id="element-493" data-mp="1o477dkq0bg7c">
                  That can include a historical display name against those
                  records, so the group can still tell which entries were yours,
                  and any receipt image attached to a shared expense.
                </li>
                <li data-id="element-494" data-mp="1t2ihlwk7rsq3">
                  We may keep a limited amount of information where we have a
                  legal reason to, for example to meet a legal obligation or to
                  deal with a dispute.
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4" data-id="element-495" data-mp="221m294z14g6p">
                Deleting your account cannot be undone. If you want a copy of
                your data first, email us before you delete it.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-kitty-dark mb-5" data-id="element-496" data-mp="2ps9ajrcux7ts">
              Delete it in the app
            </h2>
          </motion.div>

          <motion.ol initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-4 mb-12" data-id="element-497" data-mp="2lpcqrxbkz5o9">
            {steps.map((step, index) => <li key={step.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-7 flex gap-5" data-id="element-498" data-mp="2ve06fc0l1fi4" data-mpi={index}>
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-kitty-yellow text-kitty-dark font-bold flex items-center justify-center" data-id="element-499" data-mp="2lpi8d2jotmi6">
                  {index + 1}
                </span>
                <div data-id="element-500" data-mp="2cotvib615s6v">
                  <p className="text-lg font-bold text-kitty-dark leading-snug" data-id="element-501" data-mp="2cplf0nmcrzgs">
                    {step.title}
                  </p>
                  {step.detail && <p className="text-gray-500 leading-relaxed mt-2" data-id="element-502" data-mp="22qnv5xzvgu44">
                      {step.detail}
                    </p>}
                </div>
              </li>)}
          </motion.ol>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-gray-950 rounded-2xl p-8 md:p-10 text-white" data-id="element-503" data-mp="daaf1mk7fyve">
            <div className="w-11 h-11 rounded-2xl bg-white/[0.08] flex items-center justify-center mb-5" data-id="element-504" data-mp="4ekmmtkca48i">
              <MailIcon className="w-5 h-5 text-kitty-yellow" aria-hidden="true" data-id="element-505" data-mp="3vlf39zrj1hap" />
            </div>
            <h2 className="text-xl font-bold mb-3" data-id="element-506" data-mp="s7kq4usxr2ql">
              Or ask us to delete it
            </h2>
            <p className="text-gray-400 leading-relaxed" data-id="element-507" data-mp="n651ksrjue3g">
              If you cannot open the app, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-kitty-yellow underline underline-offset-2 transition-colors hover:text-kitty-yellow-500" data-id="element-508" data-mp="i7trcgxce1kp">
                {SUPPORT_EMAIL}
              </a>{' '}
              from the email on your KittyPot account and ask us to delete it.
              We will check it is you first, then confirm by email once it is
              done.
            </p>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-sm text-gray-500 mt-8" data-id="element-509" data-mp="32s1qsvv6gvli">
            For more on how we handle your data, read our{' '}
            <Link to="/privacy-policy" className="font-semibold text-kitty-dark underline underline-offset-2 transition-colors hover:text-kitty-yellow-700" data-id="element-510" data-mp="3gjf8e49izjdw">
              Privacy Policy
            </Link>
            .
          </motion.p>
        </div>
      </main>
      <Footer data-id="element-511" data-mp="1r62249hurla5" />
    </div>;
}